import { observer } from "mobx-react-lite"
import { formatUnits } from "viem"

import type { DecreasePositionStore } from "@/domains/thick-v2/stores/decrease-position-store"

import { DecreasePositionView } from "./decrease-position-view"

const safeFormat = (wei: bigint, decimals: number): string => {
  try {
    return formatUnits(wei, decimals)
  } catch {
    return "0"
  }
}

/**
 * Observer composite for `DecreasePositionStore`.
 *
 * Pulls the active position off the store base, renders the percent slider
 * plus projected token outputs, and delegates `submit()` back to the store.
 * No mutable state lives here — the store owns `percentToWithdraw` plus
 * all derived amounts through its computed properties.
 */
export const DecreasePositionComposite = observer(
  ({ store }: { store: DecreasePositionStore }) => {
    const status = store.resolvedStatus
    if (status.kind !== "found") {
      return (
        <div className="rounded-lg border border-p400-light bg-p400 p-4 text-t300 text-sm">
          {status.kind === "loading" && "Loading position..."}
          {status.kind === "missing" &&
            "This position was not found or is not owned by the connected wallet."}
          {status.kind === "idle" && "Select a position to continue."}
        </div>
      )
    }

    const position = status.position
    const pool = position.pool
    const token0 = pool.token0
    const token1 = pool.token1

    const projectedPrincipal0 = store.expectedAmount0
    const projectedPrincipal1 = store.expectedAmount1
    const pendingFees0 = position.pendingFees0 ?? 0n
    const pendingFees1 = position.pendingFees1 ?? 0n

    // Decrease executes `decreaseLiquidity + collect` in one multicall, so the
    // user receives withdrawn principal plus currently claimable fees.
    const projectedReceive0 = projectedPrincipal0 + pendingFees0
    const projectedReceive1 = projectedPrincipal1 + pendingFees1

    const expectedAmount0Display = safeFormat(projectedReceive0, token0.decimals)
    const expectedAmount1Display = safeFormat(projectedReceive1, token1.decimals)
    const minAmount0Display = safeFormat(store.amount0Min, token0.decimals)
    const minAmount1Display = safeFormat(store.amount1Min, token1.decimals)

    // Underlying amounts sit on the position and drive the "Available: …" line
    // so users always see real numbers in the receive rows, even at 0%.
    const underlyingAmount0Display = safeFormat(position.underlyingAmount0, token0.decimals)
    const underlyingAmount1Display = safeFormat(position.underlyingAmount1, token1.decimals)

    const amount0Usd = Number(expectedAmount0Display) * position.token0UsdPrice
    const amount1Usd = Number(expectedAmount1Display) * position.token1UsdPrice
    const amount0UsdProp = Number.isFinite(amount0Usd) ? amount0Usd : undefined
    const amount1UsdProp = Number.isFinite(amount1Usd) ? amount1Usd : undefined

    return (
      <DecreasePositionView
        token0Symbol={token0.symbol}
        token1Symbol={token1.symbol}
        expectedAmount0Display={expectedAmount0Display}
        expectedAmount1Display={expectedAmount1Display}
        expectedAmount0Usd={amount0UsdProp}
        expectedAmount1Usd={amount1UsdProp}
        underlyingAmount0Display={underlyingAmount0Display}
        underlyingAmount1Display={underlyingAmount1Display}
        minAmount0Display={minAmount0Display}
        minAmount1Display={minAmount1Display}
        displayRange={position.displayRange}
        isInRange={position.isInRange}
        percentToWithdraw={store.percentToWithdraw}
        onChangePercent={(next) => store.setPercentToWithdraw(next)}
        withdrawValueUsd={(amount0UsdProp ?? 0) + (amount1UsdProp ?? 0)}
        slippagePercent={store.slippage}
        canSubmit={store.canSubmit}
        isSubmitting={store.isSubmitting}
        submissionError={store.submissionError}
        onSubmit={() => void store.submit()}
        nfpmTokenIdPending={position.nfpmTokenIdPending}
      />
    )
  },
)
